/**
 * Paints the salon's colour onto the token channel that `@simpro/ui` reads.
 *
 * The library never names a colour: it names roles, and `tokens.css` says what a role looks like.
 * Overriding `--t-accent-*` at runtime is the mechanism that file was designed for, and it is what
 * makes one build serve Naturals in one colour and Green Trends in another without a rebuild
 * (TDD-SA FR-SA-122). Shared since 2026-10-08: the staff apps paint their header with it too.
 *
 * Only the accent scale moves. Success, warning and danger stay where the platform put them,
 * because a salon whose brand colour happened to be red must not turn every error message into
 * something that reads as its logo.
 */

export interface AccentRamp {
  50: string;
  100: string;
  300: string;
  400: string;
  500: string;
  600: string;
  onAccent: string;
  container: string;
  onContainer: string;
  ring: string;
  /** A step deeper than the accent, and deeper still until it reads at 4.5:1 on a 10% accent tint: links, prices. */
  ink: string;
  /** The accent lifted for dark grounds, light enough to carry near-black text at 4.5:1. */
  bright: string;
}

/** Near-black text the dark theme sets on `bright`. */
const DARK_TEXT_LUMINANCE = luminanceFromHex("#0f0e1d");

/** Derives a full scale from one hex colour, in OKLCH so lightness steps look even to the eye. */
export function accentRampFrom(hex: string): AccentRamp {
  const { l, c, h } = oklchFromHex(hex);

  // The brand colour is the 500 step whatever its lightness, so a salon sees its own colour on its
  // own buttons. The rest are placed around it and clamped, so a very light or very dark brand
  // still yields a scale with somewhere to put text.
  const at = (lightness: number, chroma = c) =>
    `oklch(${clamp(lightness, 0.12, 0.98).toFixed(3)} ${chroma.toFixed(3)} ${h.toFixed(1)})`;

  // Text on the brand colour is whichever of near-white or near-black the eye can actually read.
  const onAccent = l > 0.62 ? "oklch(0.18 0.02 " + h.toFixed(1) + ")" : "oklch(0.99 0 0)";

  return {
    50: at(0.97, c * 0.25),
    100: at(0.94, c * 0.4),
    300: at(0.82, c * 0.7),
    400: at(l + 0.08),
    500: at(l),
    600: at(l - 0.08),
    onAccent,
    // The container pair carries body text, so it is deliberately pale and its text dark: the
    // platform's contract is that every pair clears 4.5:1 in both modes.
    container: at(0.95, c * 0.35),
    onContainer: at(0.32, c * 0.9),
    ring: at(l),
    ink: at(inkLightness(l, c, h)),
    bright: at(brightLightness(l, c, h)),
  };
}

/** The tint is `color-mix(in oklch, accent 10%, white)`, so its lightness and chroma follow from the accent. */
function inkLightness(l: number, c: number, h: number): number {
  const tint = luminanceFromOklch(0.9 + 0.1 * l, 0.1 * c, h);
  let lightness = Math.min(l - 0.08, 0.6);
  while (lightness > 0.2 && contrast(luminanceFromOklch(lightness, c, h), tint) < 4.5) lightness -= 0.01;
  return lightness;
}

function brightLightness(l: number, c: number, h: number): number {
  let lightness = Math.max(l + 0.12, 0.7);
  while (lightness < 0.95 && contrast(luminanceFromOklch(lightness, c, h), DARK_TEXT_LUMINANCE) < 4.5)
    lightness += 0.01;
  return lightness;
}

/** Writes the ramp onto `:root`. Returns a function that puts back what was there. */
export function applyBranding(hex: string, root: HTMLElement): () => void {
  const ramp = accentRampFrom(hex);
  const entries: [string, string][] = [
    ["--t-accent-50", ramp[50]],
    ["--t-accent-100", ramp[100]],
    ["--t-accent-300", ramp[300]],
    ["--t-accent-400", ramp[400]],
    ["--t-accent-500", ramp[500]],
    ["--t-accent-600", ramp[600]],
    ["--t-on-accent", ramp.onAccent],
    ["--t-accent-container", ramp.container],
    ["--t-on-accent-container", ramp.onContainer],
    ["--t-ring", ramp.ring],
    ["--t-accent-ink", ramp.ink],
    ["--t-accent-bright", ramp.bright],
  ];

  const previous = entries.map(([name]) => [name, root.style.getPropertyValue(name)] as const);
  for (const [name, value] of entries) root.style.setProperty(name, value);

  return () => {
    for (const [name, value] of previous) {
      if (value) root.style.setProperty(name, value);
      else root.style.removeProperty(name);
    }
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Enough of sRGB to OKLCH for one colour at startup; a colour library would be a dependency for this. */
export function oklchFromHex(hex: string): { l: number; c: number; h: number } {
  const int = Number.parseInt(hex.slice(1), 16);
  const [r, g, b] = [(int >> 16) & 255, (int >> 8) & 255, int & 255].map((v) => linear(v / 255)) as [
    number,
    number,
    number,
  ];

  const lms = [
    0.412_221_470_8 * r + 0.536_332_536_3 * g + 0.051_445_992_9 * b,
    0.211_903_498_2 * r + 0.680_699_545_1 * g + 0.107_396_956_6 * b,
    0.088_302_461_9 * r + 0.281_718_837_6 * g + 0.629_978_700_5 * b,
  ].map((v) => Math.cbrt(v)) as [number, number, number];

  const l = 0.210_454_255_3 * lms[0] + 0.793_617_785 * lms[1] - 0.004_072_046_8 * lms[2];
  const a = 1.977_998_495_1 * lms[0] - 2.428_592_205 * lms[1] + 0.450_593_709_9 * lms[2];
  const bb = 0.025_904_037_1 * lms[0] + 0.782_771_766_2 * lms[1] - 0.808_675_766 * lms[2];

  const hue = (Math.atan2(bb, a) * 180) / Math.PI;

  return { l, c: Math.hypot(a, bb), h: hue < 0 ? hue + 360 : hue };
}

function linear(channel: number): number {
  return channel <= 0.040_45 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance of an OKLCH colour, clipped to sRGB as a browser would show it. */
export function luminanceFromOklch(l: number, c: number, h: number): number {
  const radians = (h * Math.PI) / 180;
  const a = c * Math.cos(radians);
  const b = c * Math.sin(radians);

  const lms = [
    l + 0.396_337_777_4 * a + 0.215_803_757_3 * b,
    l - 0.105_561_345_8 * a - 0.063_854_172_8 * b,
    l - 0.089_484_177_5 * a - 1.291_485_548 * b,
  ].map((v) => v ** 3) as [number, number, number];

  const rgb = [
    4.076_741_662_1 * lms[0] - 3.307_711_591_3 * lms[1] + 0.230_969_929_2 * lms[2],
    -1.268_438_004_6 * lms[0] + 2.609_757_401_1 * lms[1] - 0.341_319_396_5 * lms[2],
    -0.004_196_086_3 * lms[0] - 0.703_418_614_7 * lms[1] + 1.707_614_701 * lms[2],
  ].map((v) => clamp(v, 0, 1)) as [number, number, number];

  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
}

function luminanceFromHex(hex: string): number {
  const { l, c, h } = oklchFromHex(hex);
  return luminanceFromOklch(l, c, h);
}

export function contrast(one: number, two: number): number {
  return (Math.max(one, two) + 0.05) / (Math.min(one, two) + 0.05);
}
